import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { motion } from "framer-motion";
import { BellRing, Inbox as InboxIcon, Gavel, Eye, Coins, HelpCircle, AlertOctagon, Sparkles, Shield, Compass, Users, Mail, Send as SendIcon, TrendingUp, TrendingDown } from "lucide-react";
import { Link } from "react-router-dom";

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.07, delayChildren: 0.05 } },
};
const item = {
  hidden: { opacity: 0, y: 6 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: "easeOut" } },
};

function Cell({ children, span = "", testid }) {
  return (
    <motion.div variants={item} data-testid={testid}
                className={`bento-cell p-6 md:p-8 ${span}`}>
      {children}
    </motion.div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div>
      <div className="overline">{label}</div>
      <div className="font-display text-5xl mt-2" style={{ color: "var(--elle-ink)" }}>{value}</div>
      {sub && <div className="text-xs mt-1 font-mono-elle" style={{ color: "var(--elle-ink-dim)" }}>{sub}</div>}
    </div>
  );
}

const PLATFORM_LABEL = {
  twitter: "X/Twitter", instagram: "Instagram", facebook: "Facebook",
  tiktok: "TikTok", bluesky: "Bluesky",
};

export default function MorningSummary() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/summary/today").then(r => setData(r.data)).catch(e => setErr(e?.response?.data?.detail || "Unable to load summary."));
  }, []);

  if (err) return <div className="text-sm" style={{ color: "var(--elle-oxblood-bright)" }}>{err}</div>;
  if (!data) return <div className="text-sm font-mono-elle" style={{ color: "var(--elle-ink-dim)" }}>Compiling the morning…</div>;

  const totalEng = Object.values(data.engagement_by_platform || {}).reduce(
    (acc, e) => ({ likes: acc.likes + (e.likes||0), replies: acc.replies + (e.replies||0),
                   shares: acc.shares + (e.shares||0), reach: acc.reach + (e.reach||0) }),
    { likes: 0, replies: 0, shares: 0, reach: 0 }
  );

  return (
    <div data-testid="morning-summary-page">
      <div className="mb-8 md:mb-12">
        <div className="overline">{new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}</div>
        <h1 className="font-display text-4xl md:text-5xl mt-2" style={{ color: "var(--elle-ink)" }}>
          Good morning.
        </h1>
        <p className="mt-3 max-w-2xl" style={{ color: "var(--elle-ink-dim)" }}>
          Quiet night. Six posts sat up with readers, the filter held, and two matters want your attention.
        </p>
      </div>

      {data.priority_alerts?.length > 0 && (
        <div className="mb-6 p-5 rounded-sm"
             style={{ border: "1px solid var(--elle-oxblood)", background: "rgba(139,42,42,0.08)" }}
             data-testid="priority-alerts">
          <div className="flex items-center gap-2 overline" style={{ color: "var(--elle-oxblood-bright)" }}>
            <AlertOctagon size={14}/> Priority alert
          </div>
          {data.priority_alerts.map((a, i) => (
            <div key={i} className="mt-2 text-sm">{a}</div>
          ))}
        </div>
      )}

      {/* ========= Friday newsletter nudge ========= */}
      {data.newsletter_friday?.due_today && !data.newsletter_friday?.recent_sent && (
        <div className="mb-6 p-5 rounded-sm flex items-start gap-3 flex-wrap"
             data-testid="friday-nudge"
             style={{ border: "1px solid var(--elle-amber)",
                      background: "rgba(197,160,89,0.08)" }}>
          <Mail size={18} className="mt-1" style={{ color: "var(--elle-amber)" }}/>
          <div className="flex-1 min-w-[220px]">
            <div className="overline" style={{ color: "var(--elle-amber)" }}>
              Friday · newsletter day
            </div>
            <div className="font-display text-2xl mt-1" style={{ color: "var(--elle-ink)" }}>
              {data.newsletter_friday.draft
                ? <>Continue this week's issue: <span style={{ color: "var(--elle-amber)" }}>{data.newsletter_friday.draft.subject}</span></>
                : "Time to write this week's issue."}
            </div>
            <div className="text-sm mt-1" style={{ color: "var(--elle-ink-dim)" }}>
              Aim for Friday send so subscribers open it over the weekend.
            </div>
          </div>
          <Link to={data.newsletter_friday.draft
                    ? `/dashboard/newsletter`
                    : `/dashboard/newsletter`}
                className="btn-amber rounded-sm px-4 py-2 text-sm inline-flex items-center gap-2"
                data-testid="friday-nudge-cta">
            <Mail size={14}/>{data.newsletter_friday.draft ? "Open draft" : "Compose"}
          </Link>
        </div>
      )}

      <motion.div variants={container} initial="hidden" animate="show"
                  className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Posts sent */}
        <Cell span="md:col-span-2" testid="cell-posts">
          <div className="flex items-center justify-between">
            <Stat label="Posts sent, last 24h"
                  value={data.posts_sent_yesterday.length}
                  sub={`Across ${new Set(data.posts_sent_yesterday.map(p=>p.platform)).size} platforms`} />
            <Sparkles size={20} style={{ color: "var(--elle-amber)", opacity: 0.6 }}/>
          </div>
          <div className="mt-6 space-y-3 max-h-48 overflow-y-auto pr-2">
            {data.posts_sent_yesterday.map((p, i) => (
              <div key={i} className="flex gap-3 text-sm">
                <span className="font-mono-elle text-xs mt-0.5 shrink-0 w-20"
                      style={{ color: "var(--elle-amber)" }}>
                  {PLATFORM_LABEL[p.platform] || p.platform}
                </span>
                <span style={{ color: "var(--elle-ink)" }}>{p.content_preview}</span>
              </div>
            ))}
            {data.posts_sent_yesterday.length === 0 && (
              <div className="font-mono-elle text-sm" style={{ color: "var(--elle-ink-faint)" }}>Nothing yet today.</div>
            )}
          </div>
        </Cell>

        {/* Engagement */}
        <Cell testid="cell-engagement">
          <Stat label="Reach, last 24h" value={totalEng.reach.toLocaleString()}
                sub={`${totalEng.replies} replies · ${totalEng.likes} likes · ${totalEng.shares} shares`} />
          <Eye size={18} className="mt-4" style={{ color: "var(--elle-amber)", opacity: 0.5 }}/>
        </Cell>

        {/* Abuse filter */}
        <Cell testid="cell-abuse">
          <div className="overline" style={{ color: "var(--elle-oxblood-bright)" }}>Abuse filter</div>
          <div className="font-display text-5xl mt-2">{data.abuse_report.total}</div>
          <div className="text-xs mt-1 font-mono-elle" style={{ color: "var(--elle-ink-dim)" }}>
            blocked · content never retained
          </div>
          <div className="mt-4 space-y-1 text-xs font-mono-elle">
            {Object.entries(data.abuse_report.by_category).map(([k,v]) => (
              <div key={k} className="flex justify-between">
                <span style={{ color: "var(--elle-ink-dim)" }}>{k.replace(/_/g," ")}</span>
                <span style={{ color: "var(--elle-ink)" }}>{v}</span>
              </div>
            ))}
            {data.abuse_report.total === 0 && <div style={{ color: "var(--elle-ink-faint)" }}>All quiet.</div>}
          </div>
        </Cell>

        {/* Inbox */}
        <Cell testid="cell-inbox">
          <Link to="/dashboard/evening" className="block">
            <div className="flex items-start justify-between">
              <div>
                <div className="overline">Evening review</div>
                <div className="font-display text-5xl mt-2">{data.filtered_replies_pending}</div>
                <div className="text-xs mt-1 font-mono-elle" style={{ color: "var(--elle-ink-dim)" }}>
                  waiting for you · {data.ambiguous_flagged} flagged
                </div>
              </div>
              <InboxIcon size={18} style={{ color: "var(--elle-amber)" }}/>
            </div>
          </Link>
        </Cell>

        {/* Approvals */}
        <Cell testid="cell-approvals">
          <Link to="/dashboard/approvals" className="block">
            <div className="flex items-start justify-between">
              <div>
                <div className="overline">Pending proposals</div>
                <div className="font-display text-5xl mt-2">{data.pending_proposals}</div>
                <div className="text-xs mt-1 font-mono-elle" style={{ color: "var(--elle-ink-dim)" }}>
                  held indefinitely
                </div>
              </div>
              <Gavel size={18} style={{ color: "var(--elle-amber)" }}/>
            </div>
          </Link>
        </Cell>

        {/* Tier 3 */}
        <Cell testid="cell-tier3">
          <Link to="/dashboard/tier3" className="block">
            <div className="flex items-start justify-between">
              <div>
                <div className="overline">Tier-3 questions</div>
                <div className="font-display text-5xl mt-2">{data.tier3_flagged}</div>
                <div className="text-xs mt-1 font-mono-elle" style={{ color: "var(--elle-ink-dim)" }}>
                  deflection drafted · your call
                </div>
              </div>
              <HelpCircle size={18} style={{ color: "var(--elle-amber)" }}/>
            </div>
          </Link>
        </Cell>

        {/* Substack */}
        <Cell testid="cell-substack">
          <Link to="/dashboard/substack" className="block">
            <div className="flex items-start justify-between">
              <div>
                <div className="overline">Substack</div>
                <div className="font-display text-5xl mt-2">
                  {data.substack?.has_data ? data.substack.current : "—"}
                </div>
                <div className="text-xs mt-1 font-mono-elle flex items-center gap-1"
                     style={{ color: (data.substack?.weekly_delta || 0) > 0
                                      ? "var(--elle-moss)"
                                      : (data.substack?.weekly_delta || 0) < 0
                                        ? "var(--elle-oxblood)"
                                        : "var(--elle-ink-dim)" }}>
                  {data.substack?.has_data ? (
                    <>
                      {data.substack.weekly_delta > 0
                        ? <TrendingUp size={12}/>
                        : data.substack.weekly_delta < 0
                          ? <TrendingDown size={12}/>
                          : null}
                      {data.substack.weekly_delta > 0 ? "+" : ""}{data.substack.weekly_delta} this week
                    </>
                  ) : "log today's count →"}
                </div>
              </div>
              <SendIcon size={18} style={{ color: "var(--elle-amber)" }}/>
            </div>
          </Link>
        </Cell>

        {/* Costs */}
        <Cell span="md:col-span-2" testid="cell-costs">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="overline">LLM spend today</div>

              {/* Direct Anthropic actual spend (GBP, real money off your card) */}
              <div className="font-display text-5xl mt-2">
                £{(data.costs.total || 0).toFixed(4)}
              </div>
              <div className="text-xs mt-1 font-mono-elle" style={{ color: "var(--elle-ink-dim)" }}>
                direct Anthropic · {data.costs.call_count || 0} calls · month to date £{(data.costs.monthly_running || 0).toFixed(2)}
              </div>

              {/* Emergent-equivalent: how much Emergent WOULD have charged */}
              {data.costs.total_emergent_credits > 0 && (
                <div className="mt-4 p-3 rounded-sm"
                     data-testid="emergent-savings-block"
                     style={{ borderLeft: "3px solid var(--elle-moss)",
                              background: "rgba(61,90,64,0.08)" }}>
                  <div className="overline" style={{ color: "var(--elle-moss)" }}>
                    Emergent equivalent (what you'd have paid)
                  </div>
                  <div className="flex items-baseline gap-3 mt-1">
                    <span className="font-display text-2xl" style={{ color: "var(--elle-ink)" }}>
                      {data.costs.total_emergent_credits} credits
                    </span>
                    <span className="font-mono-elle text-sm" style={{ color: "var(--elle-ink-dim)" }}>
                      £{(data.costs.total_emergent_gbp || 0).toFixed(2)} at 20p/credit
                    </span>
                  </div>
                  <div className="mt-2 text-xs" style={{ color: "var(--elle-moss)" }}>
                    Saved today: <strong>£{Math.max(0, (data.costs.total_emergent_gbp || 0) - (data.costs.total || 0)).toFixed(2)}</strong>
                    {" · "}
                    {(data.costs.by_path?.["direct-anthropic"] || 0)} of {data.costs.call_count || 0} calls went direct
                    {(data.costs.by_path?.["emergent-fallback"] || 0) > 0 && (
                      <span style={{ color: "var(--elle-oxblood-bright)" }}>
                        {" "}· {data.costs.by_path["emergent-fallback"]} fell back to Emergent
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Last call breakdown — show her exactly what the most recent call cost */}
              {data.costs.last_llm_purpose && (
                <div className="mt-3 text-xs font-mono-elle"
                     style={{ color: "var(--elle-ink-faint)" }}
                     data-testid="last-llm-call">
                  Last: {data.costs.last_llm_purpose} ·
                  {" "}{data.costs.last_llm_tokens_in || 0} in / {data.costs.last_llm_tokens_out || 0} out ·
                  {" "}£{(data.costs.last_llm_cost_gbp || 0).toFixed(4)}
                  {" "}<span style={{ color: "var(--elle-moss)" }}>
                    (would have been {data.costs.last_llm_emergent_credits || 3} Emergent credits)
                  </span>
                </div>
              )}

              {data.costs.per_api && Object.keys(data.costs.per_api).length > 0 && (
                <div className="mt-4 space-y-1 text-xs font-mono-elle max-h-48 overflow-y-auto pr-2"
                     data-testid="per-api-costs">
                  {Object.entries(data.costs.per_api)
                    .sort((a, b) => b[1] - a[1])
                    .map(([k, v]) => (
                      <div key={k} className="flex justify-between">
                        <span style={{ color: "var(--elle-ink-dim)" }}>{k}</span>
                        <span style={{ color: "var(--elle-ink)" }}>£{v.toFixed(4)}</span>
                      </div>
                    ))}
                </div>
              )}
            </div>
            <Coins size={18} style={{ color: "var(--elle-amber)" }}/>
          </div>
        </Cell>

        {/* Trend */}
        <Cell span="md:col-span-2" testid="cell-trend">
          <div className="flex items-center justify-between">
            <div className="overline">Trend observations</div>
            <BellRing size={16} style={{ color: "var(--elle-amber)", opacity: 0.6 }}/>
          </div>
          <ul className="mt-4 space-y-2">
            {data.trend_observations.length === 0 && (
              <li className="font-mono-elle text-sm" style={{ color: "var(--elle-ink-faint)" }}>
                Nothing statistically significant yet.
              </li>
            )}
            {data.trend_observations.map((t, i) => (
              <li key={i} className="text-sm leading-relaxed" style={{ color: "var(--elle-ink)" }}>
                · {t}
              </li>
            ))}
          </ul>
        </Cell>

        {/* Standing rules — what's watching on Elle's behalf */}
        {data.standing_rules && data.standing_rules.length > 0 && (
          <Cell span="md:col-span-2" testid="cell-standing-rules">
            <div className="flex items-center justify-between">
              <div className="overline">Standing rules · watching on your behalf</div>
              <Shield size={16} style={{ color: "var(--elle-moss)", opacity: 0.8 }}/>
            </div>
            <ul className="mt-4 space-y-3">
              {data.standing_rules.map((r) => (
                <li key={r.id} data-testid={`standing-rule-${r.id}`}
                    className="border-l-2 pl-4"
                    style={{ borderColor: "var(--elle-moss)" }}>
                  <div className="font-display text-lg leading-tight" style={{ color: "var(--elle-ink)" }}>
                    {r.name}
                  </div>
                  <div className="text-xs mt-1 leading-relaxed" style={{ color: "var(--elle-ink-dim)" }}>
                    {r.description}
                  </div>
                </li>
              ))}
            </ul>
          </Cell>
        )}

        {/* Discovery — observation-mode sampling of the Bluesky feed */}
        {data.discovery && (
          <Cell span="md:col-span-2" testid="cell-discovery">
            <div className="flex items-center justify-between">
              <div className="overline">Discovery · passive observation</div>
              <Compass size={16} style={{ color: "var(--elle-amber)", opacity: 0.8 }}/>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div>
                <div className="font-display text-5xl" style={{ color: "var(--elle-ink)" }}>
                  {data.discovery.total_observed}
                </div>
                <div className="text-xs mt-1 font-mono-elle" style={{ color: "var(--elle-ink-dim)" }}>
                  posts sampled, 24h · metadata only
                </div>
              </div>
              <div className="text-xs font-mono-elle space-y-1">
                {Object.entries(data.discovery.by_source_hashtag || {}).map(([k, v]) => (
                  <div key={k} className="flex justify-between">
                    <span style={{ color: "var(--elle-ink-dim)" }}>{k}</span>
                    <span style={{ color: "var(--elle-ink)" }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
            {data.discovery.suggested_hashtags?.length > 0 && (
              <div className="mt-4 p-3 rounded-sm"
                   data-testid="morning-suggestions-block"
                   style={{ border: "1px solid var(--elle-amber)",
                            background: "rgba(197,160,89,0.06)" }}>
                <div className="overline flex items-center gap-2"
                     style={{ color: "var(--elle-amber-bright)" }}>
                  <span>The agent noticed ({data.discovery.suggested_hashtags.length})</span>
                </div>
                <div className="mt-2 text-sm" style={{ color: "var(--elle-ink)" }}>
                  Not sampling yet:{" "}
                  <span style={{ color: "var(--elle-amber-bright)" }}
                        className="font-mono-elle">
                    {data.discovery.suggested_hashtags.join("  ·  ")}
                  </span>
                </div>
                <div className="mt-2 text-xs" style={{ color: "var(--elle-ink-dim)" }}>
                  Head to <span style={{ color: "var(--elle-amber)" }}>Settings → Discovery</span> to add any you like.
                </div>
              </div>
            )}
          </Cell>
        )}

        {/* Network — autonomy shortlist / follow state */}
        {data.network && (
          <Cell span="md:col-span-2" testid="cell-network">
            <div className="flex items-center justify-between">
              <div className="overline">
                Network · {data.network.paused ? "autonomy paused" : `≤${data.network.follow_cap_per_week}/week`}
              </div>
              <Users size={16} style={{ color: "var(--elle-moss)", opacity: 0.8 }}/>
            </div>

            {/* Real Bluesky profile stats — what bsky.app shows */}
            {data.network.bluesky_profile && (
              <div className="mt-4 flex flex-wrap gap-6 text-sm pb-4 mb-4"
                   style={{ borderBottom: "1px solid rgba(210,185,120,0.15)" }}
                   data-testid="bluesky-profile-stats">
                <div>
                  <div className="font-display text-4xl" style={{ color: "var(--elle-ink)" }}>
                    {data.network.bluesky_profile.followers}
                  </div>
                  <div className="overline mt-1">followers</div>
                </div>
                <div>
                  <div className="font-display text-4xl" style={{ color: "var(--elle-ink)" }}>
                    {data.network.bluesky_profile.following}
                  </div>
                  <div className="overline mt-1">following</div>
                </div>
                <div>
                  <div className="font-display text-4xl" style={{ color: "var(--elle-ink-dim)" }}>
                    {data.network.bluesky_profile.posts}
                  </div>
                  <div className="overline mt-1">posts</div>
                </div>
                <div className="flex-1 min-w-[12rem] text-xs font-mono-elle self-end"
                     style={{ color: "var(--elle-ink-faint)" }}>
                  Live from @{data.network.bluesky_profile.handle}
                </div>
              </div>
            )}

            <div className="text-xs font-mono-elle mb-3" style={{ color: "var(--elle-ink-faint)" }}>
              Agent autonomy state
            </div>
            <div className="flex flex-wrap gap-6 text-sm">
              <div>
                <div className="font-display text-4xl" style={{ color: "var(--elle-ink)" }}>
                  {data.network.by_status?.followed || 0}
                </div>
                <div className="overline mt-1">auto-followed</div>
              </div>
              <div>
                <div className="font-display text-4xl" style={{ color: "var(--elle-ink-dim)" }}>
                  {data.network.by_status?.pending || 0}
                </div>
                <div className="overline mt-1">awaiting verify</div>
              </div>
              <div>
                <div className="font-display text-4xl"
                     style={{ color: (data.network.by_status?.unfollowed || 0) > 0
                       ? "var(--elle-oxblood-bright)" : "var(--elle-ink-faint)" }}>
                  {data.network.by_status?.unfollowed || 0}
                </div>
                <div className="overline mt-1">auto-unfollowed</div>
              </div>
              <div>
                <div className="font-display text-4xl" style={{ color: "var(--elle-ink-faint)" }}>
                  {data.network.shortlist_size}
                </div>
                <div className="overline mt-1">shortlist</div>
              </div>
            </div>
            {data.network.last_run_summary && (data.network.last_run_summary.followed > 0 ||
              data.network.last_run_summary.unfollowed > 0) && (
              <div className="mt-4 pt-4 border-t text-xs font-mono-elle"
                   style={{ borderColor: "rgba(210,185,120,0.15)", color: "var(--elle-ink-dim)" }}>
                Last run: +{data.network.last_run_summary.followed} followed,{" "}
                {data.network.last_run_summary.unfollowed} unfollowed
                {data.network.last_run_summary.errors?.length > 0 && (
                  <span style={{ color: "var(--elle-oxblood-bright)" }}>
                    {" "}· {data.network.last_run_summary.errors.length} error(s)
                  </span>
                )}
              </div>
            )}
          </Cell>
        )}
      </motion.div>
    </div>
  );
}
